/**
 * Main Layout Component
 * Phase 5.3.1: Skip-to-content link for accessibility
 * Phase 5.4: Mobile Responsiveness - Responsive sidebar
 */

import { ReactNode } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { SkipToContent } from '../components/SkipToContent';
import { useMobileMenu, useBodyScrollLock } from '../hooks/useMobileMenu';

interface MainLayoutProps {
  children: ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const { isOpen, isMobile, openMenu, closeMenu } = useMobileMenu();

  // Lock body scroll when mobile menu is open
  useBodyScrollLock(isMobile && isOpen);

  return (
    <div className="flex h-screen bg-background">
      {/* Skip link for keyboard users - must be first focusable element */}
      <SkipToContent targetId="main-content" />

      {/* Desktop sidebar (hidden on mobile) */}
      <Sidebar isOpen={isOpen} onClose={closeMenu} isMobile={isMobile} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <Header onMenuClick={isMobile ? openMenu : undefined} />
        <main
          id="main-content"
          className="flex-1 overflow-auto p-4 md:p-6"
          role="main"
          aria-label="Main content"
          tabIndex={-1}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
